<template>
  <main class="page-leads">
    <div class="container-wide">
      <DownloadChromePluginPanel v-if="false" />
      <LeadsHeadline :pageKind="pageKind" :kindPrefix="kindPrefix" />
      <mq-layout mq="lg">
        <LeadsFilters :pageKind="pageKind" :kindPrefix="kindPrefix" />
      </mq-layout>
      <LeadsGraphs v-show="show && !graphsHiddenOnScroll && showGraphsPermission" :pageKind="pageKind"
        :kindPrefix="kindPrefix" />
      <LeadsGrid :pageKind="pageKind" :kindPrefix="kindPrefix" @toggle-lead-edit="toggleLeadEdit"
        @toggle-leadclient-edit="toggleLeadClientEdit" @toggle-lead-history="toggleLeadHistory" />
    </div>
    <transition name="fade">
      <LeadClientsGridContentEdit v-if="leadClientEditVisible" @toggle-leadclient-edit2="toggleLeadClientEdit" />
      <LeadsGridContentEdit v-if="leadEditVisible" :mode="toggleLeadEditMode" @toggle-lead-edit2="toggleLeadEdit" />
      <LeadsGridContentHistory v-if="leadHistoryVisible" :pageKind="pageKind" @toggle-lead-history2="toggleLeadHistory" />
    </transition>
  </main>
</template>

<script lang="ts">

import { CancelTokenSource } from "axios";
import moment from "moment";
import VueMq from "vue-mq";
import { Component, Vue, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import i18n from "@/i18n";
import LeadClientsGridContentEdit from "@/components/Leads/LeadClientsGridContentEdit.vue";
import LeadsGridContentEdit from "@/components/Leads/LeadsGridContentEdit.vue";
import LeadsGridContentHistory from "@/components/Leads/LeadsGridContentHistory.vue";
import { ChannelMetadataType } from "@/models/campaigns/ChannelMetadataType";
import { LeadsPageViewModel, RefreshGridMode, ToggleLeadEditMode, PhonePopupData } from "@/models/leads/ContactForm";
import { DataSourceResult } from "@/models/DataSourceResult";
import { FilterCollection } from "@/models/FilterCollection";
import { LeadPermission, LeadPermissionType } from "@/models/leads/LeadPermission";
import { LeadStatistic, LeadStatisticExtended } from "@/models/leads/LeadStatistic";
import { LeadsPageKind } from "@/models/LeadsPageKind";
import { LeadStatus } from "@/models/leads/LeadStatus";
import { MetadataMaskyooStatus } from "@/models/MetadataMaskyooStatus";
import { ChannelMultiselectItem, MultiselectItem } from "@/models/MultiselectItem";
import { TableSettings, TableSettingsResponse, TableSettingsState } from "@/models/TableSettings";
import ApiService, { CommonMultiselectPayload, LeadsPayload, LeadsStatisticByCampaignsPayload } from "@/services/api/ApiService";
import HelperService from "@/services/HelperService";
import LeadService from "@/services/LeadService";
import DownloadChromePluginPanel from "../components/DownloadChromePluginPanel.vue";
import LeadsHeadline from "../components/Leads/LeadsHeadline.vue";
import LeadsFilters from "../components/Leads/LeadsFilters.vue";
import LeadsGraphs from "../components/Leads/LeadsGraphs.vue";
import LeadsGrid from "../components/Leads/LeadsGrid.vue";
import GridTableSettingsEn from "../data/grid-defaultsettings-en.json";
import GridTableSettingsHe from "../data/grid-defaultsettings-he.json";
import { Meta } from "../decorators/MetaDecorator";
import PromiseQueue from "../helpers/PromiseQueue";

Vue.use(VueMq, {
  breakpoints: {
    sm: 576,
    md: 992,
    lg: Infinity,
  },
  defaultBreakpoint: "sm",
});
const namespace = "leadsModule";

@Component({
  components: {
    DownloadChromePluginPanel,
    LeadsHeadline,
    LeadsFilters,
    LeadsGraphs,
    LeadsGrid,
    LeadClientsGridContentEdit,
    LeadsGridContentEdit,
    LeadsGridContentHistory
  },
})
export default class Leads extends Vue {

  @Meta
  myMetaConfig() {
    return {
      title: i18n.t(`${this.kindPrefix}.Title`).toString()
    };
  }

  //#region Vuex getters and actions

  @Getter("columnsFilters", { namespace }) columnsFilters!: FilterCollection;
  @Getter("datePeriod", { namespace }) datePeriod!: Date[];
  @Getter("graphsHiddenOnScroll", { namespace }) graphsHiddenOnScroll!: boolean;
  @Getter("leads", { namespace }) leads!: DataSourceResult;
  @Getter("leadsCancelTokenSource", { namespace }) leadsCancelTokenSource!: CancelTokenSource | undefined;
  @Getter("leadClientsDatePeriod", { namespace }) leadClientsDatePeriod!: Date[];
  @Getter("leadClientsSearchValue", { namespace }) leadClientsSearchValue!: string;
  @Getter("leadClientsSelectedAdditionalStatusesIds", { namespace }) leadClientsSelectedAdditionalStatusesIds!: number[];
  @Getter("leadClientsSelectedAdGroupsIds", { namespace }) leadClientsSelectedAdGroupsIds!: string[];
  @Getter("leadClientsSelectedAdTitles", { namespace }) leadClientsSelectedAdTitles!: string[];
  @Getter("leadClientsSelectedAssetsIds", { namespace }) leadClientsSelectedAssetsIds!: string[];
  @Getter("leadClientsSelectedAssigneesIds", { namespace }) leadClientsSelectedAssigneesIds!: number[];
  @Getter("leadClientsSelectedCallStatusesIds", { namespace }) leadClientsSelectedCallStatusesIds!: number[];
  @Getter("leadClientsSelectedChannelMetadataTypesIds", { namespace }) leadClientsSelectedChannelMetadataTypesIds!: number[];
  @Getter("leadClientsSelectedChannelsIds", { namespace }) leadClientsSelectedChannelsIds!: number[];
  @Getter("leadClientsSelectedClientsIds", { namespace }) leadClientsSelectedClientsIds!: number[];
  @Getter("leadClientsSelectedCommitmentsIds", { namespace }) leadClientsSelectedCommitmentsIds!: number[];
  @Getter("leadClientsSelectedFoundMetadataTypesIds", { namespace }) leadClientsSelectedFoundMetadataTypesIds!: number[];
  @Getter("leadClientsSelectedLeadStatusesIds", { namespace }) leadClientsSelectedLeadStatusesIds!: LeadStatus[];
  @Getter("leadClientsSelectedPriceType", { namespace }) leadClientsSelectedPriceType!: string;
  @Getter("leadClientsSelectedSaleStatusesIds", { namespace }) leadClientsSelectedSaleStatusesIds!: LeadStatus[];
  @Getter("leadClientsSelectedTags", { namespace }) leadClientsSelectedTags!: string[];
  @Getter("leadsPageData", { namespace }) leadsPageData!: LeadsPageViewModel;
  @Getter("needToRefreshLeadsGrid", { namespace }) needToRefreshLeadsGrid!: RefreshGridMode;
  @Getter("page", { namespace }) page!: number;
  @Getter("searchValue", { namespace }) searchValue!: string;
  @Getter("selectedAdditionalStatusesIds", { namespace }) selectedAdditionalStatusesIds!: number[];
  @Getter("selectedAdGroupsIds", { namespace }) selectedAdGroupsIds!: string[];
  @Getter("selectedAdTitles", { namespace }) selectedAdTitles!: string[];
  @Getter("selectedAssetsIds", { namespace }) selectedAssetsIds!: string[];
  @Getter("selectedAssigneesIds", { namespace }) selectedAssigneesIds!: number[];
  @Getter("selectedCallStatusesIds", { namespace }) selectedCallStatusesIds!: number[];
  @Getter("selectedChannelMetadataTypesIds", { namespace }) selectedChannelMetadataTypesIds!: number[];
  @Getter("selectedChannelsIds", { namespace }) selectedChannelsIds!: number[];
  @Getter("selectedClientsIds", { namespace }) selectedClientsIds!: number[];
  @Getter("selectedCommitmentsIds", { namespace }) selectedCommitmentsIds!: number[];
  @Getter("selectedFoundMetadataTypesIds", { namespace }) selectedFoundMetadataTypesIds!: number[];
  @Getter("selectedLeadStatusesIds", { namespace }) selectedLeadStatusesIds!: LeadStatus[];
  @Getter("selectedPriceType", { namespace }) selectedPriceType!: string;
  @Getter("selectedSaleStatusesIds", { namespace }) selectedSaleStatusesIds!: LeadStatus[];
  @Getter("selectedTags", { namespace }) selectedTags!: string[];
  @Getter("showDeleted", { namespace }) showDeleted!: boolean;
  @Getter("showFavourite", { namespace }) showFavourite!: boolean;
  @Getter("showGraphs", { namespace }) showGraphs!: boolean;
  @Getter("showGraphsClientLeads", { namespace }) showGraphsClientLeads!: boolean;
  @Getter("showNotActiveCampaign", { namespace }) showNotActiveCampaign!: boolean;
  @Getter("sortDir", { namespace }) sortDir!: string;
  @Getter("sortField", { namespace }) sortField!: string;
  @Getter("tags", { namespace }) tags!: MultiselectItem[];
  @Getter("uniqueByCampaign", { namespace }) uniqueByCampaign!: boolean;
  @Action("getChannels", { namespace }) getChannels!: (payload: CommonMultiselectPayload) => Promise<ChannelMultiselectItem[]>;
  @Action("getClients", { namespace }) getClients!: () => Promise<MultiselectItem[]>;
  @Action("getCommitments", { namespace }) getCommitments!: (payload: CommonMultiselectPayload) => Promise<MultiselectItem[]>;
  @Action("getLeads", { namespace }) getLeads!: (payload: LeadsPayload) => Promise<DataSourceResult>;
  @Action("getLeadsPageData", { namespace }) getLeadsPageData!: () => Promise<LeadsPageViewModel>;
  @Action("checkPhonePopup", { namespace }) checkPhonePopup!: () => Promise<PhonePopupData>;
  @Action("getLeadsStatisticByAdditionalStatus", { namespace }) getLeadsStatisticByAdditionalStatus!: (payload: LeadsPayload) => Promise<LeadStatistic[]>;
  @Action("getLeadsStatisticByCallStatus", { namespace }) getLeadsStatisticByCallStatus!: (payload: LeadsPayload) => Promise<LeadStatistic[]>;
  @Action("getLeadsStatisticByCampaigns", { namespace }) getLeadsStatisticByCampaigns!: (payload: LeadsStatisticByCampaignsPayload) => Promise<LeadStatistic[]>;
  @Action("getLeadsStatisticByPhoneAndForms", { namespace }) getLeadsStatisticByPhoneAndForms!: (payload: LeadsPayload) => Promise<LeadStatistic[]>;
  @Action("getLeadsStatisticBySaleStatus", { namespace }) getLeadsStatisticBySaleStatus!: (payload: LeadsPayload) => Promise<LeadStatistic[]>;
  @Action("getLeadsStatisticChannelMetadataType", { namespace }) getLeadsStatisticChannelMetadataType!: (payload: LeadsPayload) => Promise<LeadStatistic[]>;
  @Action("getLeadsStatisticDealMeetingsLeads", { namespace }) getLeadsStatisticDealMeetingsLeads!: (payload: LeadsPayload) => Promise<LeadStatisticExtended[]>;
  @Action("getTags", { namespace }) getTags!: (payload: CommonMultiselectPayload) => Promise<MultiselectItem[]>;
  @Action("setColumnsFilters", { namespace }) setColumnsFilters!: (columnsFilters: FilterCollection) => void;
  @Action("setDeactivateStats", { namespace }) setDeactivateStats!: (deactivateStats: boolean) => void;
  @Action("setLeadClientsSelectedAssetsIds", { namespace }) setLeadClientsSelectedAssetsIds!: (selectedAssetsIds: string[]) => void;
  @Action("setLeadClientsSelectedCallStatusesIds", { namespace }) setLeadClientsSelectedCallStatusesIds!: (selectedCallStatusesIds: number[]) => void;
  @Action("setLeadClientsSelectedChannelsIds", { namespace }) setLeadClientsSelectedChannelsIds!: (selectedChannelsIds: number[]) => void;
  @Action("setLeadClientsSelectedChannelMetadataTypesIds", { namespace }) setLeadClientsSelectedChannelMetadataTypesIds!: (selectedChannelMetadataTypesIds: number[]) => void;
  @Action("setLeadClientsSelectedClientsIds", { namespace }) setLeadClientsSelectedClientsIds!: (selectedClientsIds: number[]) => void;
  @Action("setLeadClientsSelectedCommitmentsIds", { namespace }) setLeadClientsSelectedCommitmentsIds!: (selectedCommitmentsIds: number[]) => void;
  @Action("setLeadClientsSelectedFoundMetadataTypesIds", { namespace }) setLeadClientsSelectedFoundMetadataTypesIds!: (selectedFoundMetadataTypesIds: number[]) => void;
  @Action("setLeadClientsSelectedLeadStatusesIds", { namespace }) setLeadClientsSelectedLeadStatusesIds!: (selectedLeadStatusesIds: LeadStatus[]) => void;
  @Action("setLeadClientsSelectedPriceType", { namespace }) setLeadClientsSelectedPriceType!: (selectedPriceType: string) => void;
  @Action("setLeadClientsSelectedSaleStatusesIds", { namespace }) setLeadClientsSelectedSaleStatusesIds!: (selectedSaleStatusesIds: LeadStatus[]) => void;
  @Action("setLeadClientsSelectedTags", { namespace }) setLeadClientsSelectedTags!: (selectedTags: string[]) => void;
  @Action("setLeadsStatisticByAdditionalStatus", { namespace }) setLeadsStatisticByAdditionalStatus!: (data: LeadStatistic[] | null) => void;
  @Action("setLeadsStatisticByCallStatus", { namespace }) setLeadsStatisticByCallStatus!: (data: LeadStatistic[] | null) => void;
  @Action("setLeadsStatisticByCampaigns", { namespace }) setLeadsStatisticByCampaigns!: (data: LeadStatistic[] | null) => void;
  @Action("setLeadsStatisticByPhoneAndForms", { namespace }) setLeadsStatisticByPhoneAndForms!: (data: LeadStatistic[] | null) => void;
  @Action("setLeadsStatisticBySaleStatus", { namespace }) setLeadsStatisticBySaleStatus!: (data: LeadStatistic[] | null) => void;
  @Action("setLeadsStatisticChannelMetadataType", { namespace }) setLeadsStatisticChannelMetadataType!: (data: LeadStatistic[] | null) => void;
  @Action("setLeadsStatisticDealMeetingsLeads", { namespace }) setLeadsStatisticDealMeetingsLeads!: (data: LeadStatistic[] | null) => void;
  @Action("setLeadsCancelTokenSource", { namespace }) setLeadsCancelTokenSource!: (cancelTokenSource: CancelTokenSource) => void;
  @Action("setLeadsPayload", { namespace }) setLeadsPayload!: (leadsPayload: LeadsPayload) => void;
  @Action("setNeedToRefreshLeadsGrid", { namespace }) setNeedToRefreshLeadsGrid!: (needToRefreshLeadsGrid: RefreshGridMode) => void;
  @Action("setPage", { namespace }) setPage!: (page: number) => void;
  @Action("setSelectedCallStatusesIds", { namespace }) setSelectedCallStatusesIds!: (selectedCallStatusesIds: number[]) => void;
  @Action("setSelectedChannelsIds", { namespace }) setSelectedChannelsIds!: (selectedChannelsIds: number[]) => void;
  @Action("setSelectedChannelMetadataTypesIds", { namespace }) setSelectedChannelMetadataTypesIds!: (selectedChannelMetadataTypesIds: number[]) => void;
  @Action("setSelectedClientsIds", { namespace }) setSelectedClientsIds!: (selectedClientsIds: number[]) => void;
  @Action("setSelectedCommitmentsIds", { namespace }) setSelectedCommitmentsIds!: (selectedCommitmentsIds: number[]) => void;
  @Action("setSelectedFoundMetadataTypesIds", { namespace }) setSelectedFoundMetadataTypesIds!: (selectedFoundMetadataTypesIds: number[]) => void;
  @Action("setSelectedLeadStatusesIds", { namespace }) setSelectedLeadStatusesIds!: (selectedLeadStatusesIds: LeadStatus[]) => void;
  @Action("setSelectedPriceType", { namespace }) setSelectedPriceType!: (selectedPriceType: string) => void;
  @Action("setSelectedSaleStatusesIds", { namespace }) setSelectedSaleStatusesIds!: (selectedSaleStatusesIds: LeadStatus[]) => void;
  @Action("setSelectedTags", { namespace }) setSelectedTags!: (selectedTags: string[]) => void;
  @Action("setShowDeleted", { namespace }) setShowDeleted!: (showDeleted: boolean) => void;
  @Action("setShowFavourite", { namespace }) setShowFavourite!: (showFavourite: boolean) => void;
  @Action("setShowGraphs", { namespace }) setShowGraphs!: (showGraphs: boolean) => void;
  @Action("setTableSettings", { namespace }) setTableSettings!: (tableSettings: TableSettings | undefined) => void;
  @Action("setUniqueByCampaign", { namespace }) setUniqueByCampaign!: (uniqueByCampaign: boolean) => void;

  //#endregion Vuex getters and actions


  //#region Properties

  MAX_LEADS_COUNT_FOR_STATS = 200000;
  PAGE_FILTERS_COOKIE_NAME = "page_filters";

  toggleLeadEditMode = ToggleLeadEditMode.None;
  token = "";

  public leadClientEditVisible = false;
  public leadEditVisible = false;
  public leadHistoryVisible = false;

  private refreshGridQueue = new PromiseQueue();
  
  //#endregion Properties


  //#region Watchers

  @Watch("datePeriod", { immediate: true })
  onDatePeriodChange(newDatePeriod: Date[], oldDatePeriod: Date[]): void {
    if (oldDatePeriod === undefined || newDatePeriod === undefined || oldDatePeriod.length === 0
      || moment(newDatePeriod[0]).format("YYYY-MM-DD") === moment(oldDatePeriod[0]).format("YYYY-MM-DD")
      && moment(newDatePeriod[1]).format("YYYY-MM-DD") === moment(oldDatePeriod[1]).format("YYYY-MM-DD")) {
      return;
    } else {
      this.refreshGrid();
    }
  }

  @Watch("leadClientsDatePeriod", { immediate: true })
  onLeadClientsDatePeriodChange(newDatePeriod: Date[], oldDatePeriod: Date[]): void {
    if (oldDatePeriod === undefined || newDatePeriod === undefined || oldDatePeriod.length === 0
      || moment(newDatePeriod[0]).format("YYYY-MM-DD") === moment(oldDatePeriod[0]).format("YYYY-MM-DD")
      && moment(newDatePeriod[1]).format("YYYY-MM-DD") === moment(oldDatePeriod[1]).format("YYYY-MM-DD")) {
      return;
    } else {
      this.refreshGrid();
    }
  }

  @Watch("leadClientsSearchValue", { immediate: true })
  onLeadClientsSearchValueChange(newValue: string, oldValue: string): void {
    if (oldValue === undefined) {
      return;
    } else {
      this.refreshGrid();
    }
  }

  @Watch("needToRefreshLeadsGrid", { immediate: true })
  onNeedToRefreshLeadsGrid(newValue: RefreshGridMode, oldValue: RefreshGridMode): void {
    if (oldValue === undefined) {
      return;
    } else if (newValue) {
      this.refreshGrid(newValue);
    }
  }

  @Watch("searchValue", { immediate: true })
  onSearchValueChange(newValue: string, oldValue: string): void {
    if (oldValue === undefined) {
      return;
    } else {
      this.refreshGrid();
    }
  }

  @Watch("pageKind", { immediate: true })
  onPageKindChange(newValue: LeadsPageKind, oldValue: LeadsPageKind): void {
    if (oldValue === undefined) {
      return;
    } else if (newValue !== undefined) {
      const filterCollection: FilterCollection = {
        filters: [],
      };
      this.setColumnsFilters(filterCollection);

      this.getTableSettings();
      this.refreshGridQueue.stop();
      this.refreshGrid();
      this.refreshGridQueue.start();
    }
  }

  @Watch("showDeleted", { immediate: true })
  onShowDeletedChange(newShowDeletedValue: string, oldShowDeletedValue: string): void {
    if (newShowDeletedValue === oldShowDeletedValue || oldShowDeletedValue === undefined) {
      return;
    } else {
      this.refreshGrid();
    }
  }

  @Watch("showFavourite", { immediate: true })
  onShowFavouriteChange(newShowFavouriteValue: string, oldShowFavouriteValue: string): void {
    if (newShowFavouriteValue === oldShowFavouriteValue || oldShowFavouriteValue === undefined) {
      return;
    } else {
      this.refreshGrid();
    }
  }

  @Watch("showGraphs", { immediate: true })
  onShowGraphsChange(newValue: boolean, oldValue: boolean): void {
    if (newValue === oldValue || oldValue === undefined) {
      return;
    } else if (newValue) {
      this.refreshGrid(RefreshGridMode.GraphsOnly);
    }
  }

  @Watch("showGraphsClientLeads", { immediate: true })
  onShowGraphsClientLeadsChange(newValue: boolean, oldValue: boolean): void {
    if (newValue === oldValue || oldValue === undefined) {
      return;
    } else if (newValue) {
      this.refreshGrid(RefreshGridMode.GraphsOnly);
    }
  }

  @Watch("uniqueByCampaign", { immediate: true })
  onUniqueByCampaignChange(newUniqueValue: string, oldUniqueValue: string): void {
    if (newUniqueValue === oldUniqueValue || oldUniqueValue === undefined) {
      return;
    } else {
      this.refreshGrid();

      const cookieName = "do_not_show_unique_popup";
      const cookieValue: string = Vue.$cookies.get(cookieName);
      if (newUniqueValue && cookieValue !== "true") {
        HelperService.showConfirmDialog(
          undefined,
          i18n.t("Leads.UniquePopup.Text").toString(),
          i18n.t("Leads.UniquePopup.DoNotShow").toString(),
          i18n.t("OKay").toString(),
          () => {
            Vue.$cookies.set(cookieName, true, "1y");
          }
        );
      }
    }
  }

  //#endregion Watchers


  //#region Getters

  get commonPayload(): CommonMultiselectPayload {
    return {
      clientIds: this.usedSelectedClientsIds,
      showNotActiveCamp: this.showNotActiveCampaign,
      metadataIds: this.usedSelectedChannelMetadataTypesIds,
      statusIds: this.usedSelectedSaleStatusesIds.map((i: LeadStatus) => i.toString()),
      channelIds: this.usedSelectedChannelsIds,
    };
  }

  get kindPrefix(): string {
    return LeadsPageKind[this.pageKind];
  }

  get pageKind(): LeadsPageKind {
    return LeadService.getLeadsPageKind(this.$route.query.pageKind);
  }

  get show(): boolean {
    if (this.pageKind) {
      return this.showGraphsClientLeads;
    }
    return this.showGraphs;
  }

  get showChannelMetadataType(): boolean {
    return this.leadsPageData && !this.leadsPageData?.ActiveRestictions?.some((i: LeadPermission) => i.Restriction === LeadPermissionType.Source);
  }

  get showGraphsPermission(): boolean {
    return !this.leadsPageData?.ActiveRestictions?.some((i: LeadPermission) => i.Restriction === LeadPermissionType.LeadsScreenCharts);
  }

  //#region Getters based on page kind

  get usedDatePeriod(): Date[] {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        return this.leadClientsDatePeriod;
      case LeadsPageKind.Leads:
        return this.datePeriod;
    }
  }

  get usedSearchValue(): string {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        return this.leadClientsSearchValue;
      case LeadsPageKind.Leads:
        return this.searchValue;
    }
  }

  get usedSelectedAdditionalStatusesIds(): number[] {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        return this.leadClientsSelectedAdditionalStatusesIds;
      case LeadsPageKind.Leads:
        return this.selectedAdditionalStatusesIds;
    }
  }

  get usedSelectedAdGroupsIds(): string[] {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        return this.leadClientsSelectedAdGroupsIds;
      case LeadsPageKind.Leads:
        return this.selectedAdGroupsIds;
    }
  }

  get usedSelectedAdTitles(): string[] {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        return this.leadClientsSelectedAdTitles;
      case LeadsPageKind.Leads:
        return this.selectedAdTitles;
    }
  }

  get usedSelectedAssetsIds(): string[] {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        return this.leadClientsSelectedAssetsIds;
      case LeadsPageKind.Leads:
        return this.selectedAssetsIds;
    }
  }

  get usedSelectedAssigneesIds(): number[] {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        return this.leadClientsSelectedAssigneesIds;
      case LeadsPageKind.Leads:
        return this.selectedAssigneesIds;
    }
  }

  get usedSelectedCallStatusesIds(): number[] {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        return this.leadClientsSelectedCallStatusesIds;
      case LeadsPageKind.Leads:
        return this.selectedCallStatusesIds;
    }
  }

  get usedSelectedChannelMetadataTypesIds(): number[] {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        return this.leadClientsSelectedChannelMetadataTypesIds;
      case LeadsPageKind.Leads:
        return this.selectedChannelMetadataTypesIds;
    }
  }

  get usedSelectedChannelsIds(): number[] {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        return this.leadClientsSelectedChannelsIds;
      case LeadsPageKind.Leads:
        return this.selectedChannelsIds;
    }
  }

  get usedSelectedClientsIds(): number[] {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        return this.leadClientsSelectedClientsIds;
      case LeadsPageKind.Leads:
        return this.selectedClientsIds;
    }
  }

  get usedSelectedCommitmentsIds(): number[] {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        return this.leadClientsSelectedCommitmentsIds;
      case LeadsPageKind.Leads:
        return this.selectedCommitmentsIds;
    }
  }

  get usedSelectedFoundMetadataTypesIds(): number[] {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        return this.leadClientsSelectedFoundMetadataTypesIds;
      case LeadsPageKind.Leads:
        return this.selectedFoundMetadataTypesIds;
    }
  }

  get usedSelectedLeadStatusesIds(): LeadStatus[] {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        return this.leadClientsSelectedLeadStatusesIds;
      case LeadsPageKind.Leads:
        return this.selectedLeadStatusesIds;
    }
  }

  get usedSelectedPriceType(): string {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        return this.leadClientsSelectedPriceType;
      case LeadsPageKind.Leads:
        return this.selectedPriceType;
    }
  }

  get usedSelectedSaleStatusesIds(): LeadStatus[] {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        return this.leadClientsSelectedSaleStatusesIds;
      case LeadsPageKind.Leads:
        return this.selectedSaleStatusesIds;
    }
  }

  get usedSelectedTags(): string[] {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        return this.leadClientsSelectedTags;
      case LeadsPageKind.Leads:
        return this.selectedTags;
    }
  }

  //#endregion Getters based on page kind

  //#endregion Getters


  //#region Lifecycle

  mounted(): void {
    this.token = HelperService.guid();
    const cookieValue: string = Vue.$cookies.get(this.PAGE_FILTERS_COOKIE_NAME);
    if (!cookieValue) { // when cookie expires, clear filters
      LeadService.resetFilters(this.pageKind, false);
    }
    this.getLeadsPageData().then(async (i: LeadsPageViewModel) => {
      if (i.ClientId) {
        this.setLeadClientsSelectedClientsIds([i.ClientId]);
        this.setSelectedClientsIds([i.ClientId]);
      } else {
        await this.refreshGrid();
        this.getClients();
        this.getCommitments(this.commonPayload);
        this.getTags(this.commonPayload);
      }
      this.applyQueryParams();
    });
    this.getTableSettings();
    this.checkPhonePopup();
  }

  //#endregion Lifecycle


  //#region Handlers

  public popupBodyOff(): void {
    const scrollY = document.body.style.top;
    document.body.style.position = "";
    document.body.style.top = "";
    window.scrollTo(0, parseInt(scrollY || "0") * -1);
  }

  public popupBodyOn(): void {
    document.body.style.top = "-" + window.scrollY + "px";
    document.body.style.position = "fixed";
  }

  public toggleLeadClientEdit(): void {
    this.leadClientEditVisible = !this.leadClientEditVisible;
    this.leadClientEditVisible ? this.popupBodyOn() : this.popupBodyOff();
  }

  public toggleLeadEdit(mode: ToggleLeadEditMode): void {
    this.toggleLeadEditMode = mode;
    this.leadEditVisible = !this.leadEditVisible;
    this.leadEditVisible ? this.popupBodyOn() : this.popupBodyOff();
  }

  public toggleLeadHistory(): void {
    this.leadHistoryVisible = !this.leadHistoryVisible;
    this.leadHistoryVisible ? this.popupBodyOn() : this.popupBodyOff();
  }

  //#endregion Handlers


  //#region Private methods

  private applyQueryParams() {
    if (this.$route.query.callStatuses) {
      this.setUsedSelectedCallStatusesIds((this.$route.query.callStatuses as string).split(",").map(i => +i));
    }
    if (this.$route.query.channelIds) {
      this.setUsedSelectedChannelsIds((this.$route.query.channelIds as string).split(",").map(i => +i));
    }
    if (this.$route.query.channelMetadataTypes) {
      this.setUsedSelectedChannelMetadataTypesIds((this.$route.query.channelMetadataTypes as string).split(",").map(i => +i));
    }
    if (this.$route.query.clientIds) {
      this.setUsedSelectedClientsIds((this.$route.query.clientIds as string).split(",").map(i => +i));
    }
    if (this.$route.query.deleted) {
      const deleted: number = +(this.$route.query.deleted as string);
      this.setShowDeleted(deleted === 1 ? true : false);
    }
    if (this.$route.query.favourite) {
      const favourite: number = +(this.$route.query.favourite as string);
      this.setShowFavourite(favourite === 1 ? true : false);
    }
    if (this.$route.query.foundMetadataTypes) {
      this.setUsedSelectedFoundMetadataTypesIds((this.$route.query.foundMetadataTypes as string).split(",").map(i => +i));
    }
    if (this.$route.query.leadEntryStatuses) {
      this.setUsedSelectedLeadStatusesIds((this.$route.query.leadEntryStatuses as string).split(",").map(i => i as any as LeadStatus));
    }
    if (this.$route.query.priceType) {
      this.setUsedSelectedPriceType(this.$route.query.priceType as string);
    }
    if (this.$route.query.projectIds) {
      this.setUsedSelectedCommitmentsIds((this.$route.query.projectIds as string).split(",").map(i => +i));
    }
    if (this.$route.query.saleStatuses) {
      this.setUsedSelectedSaleStatusesIds((this.$route.query.saleStatuses as string).split(",").map(i => +i));
    }
    if (this.$route.query.tags) {
      this.setUsedSelectedTags((this.$route.query.tags as string).split(","));
    }
    if (this.$route.query.unique) {
      const unique: number = +(this.$route.query.unique as string);
      this.setUniqueByCampaign(unique === 1 ? true : false);
    }
  }

  private getTableSettings(): void {
    this.setTableSettings(undefined);
    ApiService.getTableSettings(i18n.locale, this.$route.path).then((data: TableSettingsResponse) => {
      if (data.settings.length > 0) {
        data.settings.forEach((settings: TableSettings) => {
          settings.StateObject = JSON.parse(settings.State);
          this.setTableSettings(settings);
        });
      } else {
        let settingsState: TableSettingsState = GridTableSettingsHe;
        switch (i18n.locale) {
          case "en-US":
            settingsState = GridTableSettingsEn;
            break;
          case "he-IL":
            settingsState = GridTableSettingsHe;
            break;
        }
        this.setTableSettings({
          Culture: i18n.locale,
          GridId: "leadsgrid",
          State: JSON.stringify(settingsState),
          StateObject: settingsState,
          Url: location.pathname
        });
      }
    });
  }

  private refreshGrid(mode: RefreshGridMode = RefreshGridMode.Full): Promise<void> {
    return this.refreshGridQueue.add(async () => {
      if (this.leadsCancelTokenSource) {
        this.leadsCancelTokenSource?.cancel();
      }
      this.setNeedToRefreshLeadsGrid(RefreshGridMode.None);
      if (mode !== RefreshGridMode.GridOnly) {
        this.setPage(1);
      }
      this.token = HelperService.guid();

      if (mode !== RefreshGridMode.GridOnly) {
        // show spinners on graphics early
        this.setLeadsStatisticByAdditionalStatus(null);
        this.setLeadsStatisticByCallStatus(null);
        this.setLeadsStatisticByCampaigns(null);
        this.setLeadsStatisticByPhoneAndForms(null);
        this.setLeadsStatisticBySaleStatus(null);
        this.setLeadsStatisticChannelMetadataType(null);
        this.setLeadsStatisticDealMeetingsLeads(null);
      }

      //#region Filters
      const filterCollection: FilterCollection = {
        logic: "and",
        filters: [],
      };
      if (this.usedSearchValue) {
        const searchFilters: FilterCollection = {
          logic: "or",
          filters: [],
        };
        var phoneText = this.usedSearchValue.replace(/\-/g, "").trim();
        if (/^-?\d+$/.test(phoneText)) {
          searchFilters.filters?.push({ field: "Phone", operator: "contains", value: phoneText });
        }
        else if (this.usedSearchValue.indexOf("@") > -1) {
          searchFilters.filters?.push({ field: "Email", operator: "contains", value: `'${this.usedSearchValue}'` });
        }
        else {
          searchFilters.filters?.push({ field: "FullName", operator: "contains", value: `'${this.usedSearchValue}'` });
          searchFilters.filters?.push({ field: "Phone", operator: "contains", value: `'${phoneText}'` });
        }
        filterCollection.filters?.push(searchFilters);
      }
      if (this.columnsFilters && this.columnsFilters.filters && this.columnsFilters.filters?.length > 0) {
        filterCollection.filters?.push(this.columnsFilters);
      }
      if (this.leadsPageData?.ClientId) {
        filterCollection.filters?.push(HelperService.setFilterArray(["ClientID"], [this.leadsPageData.ClientId], "eq"));
      } else if (this.usedSelectedClientsIds.length) {
        filterCollection.filters?.push(HelperService.setFilterArray(["ClientID"], this.usedSelectedClientsIds, "eq"));
      }
      if (this.usedSelectedChannelsIds.length) {
        filterCollection.filters?.push(HelperService.setFilterArray(["ChannelID"], this.usedSelectedChannelsIds, "eq"));
      }
      if (this.usedSelectedChannelMetadataTypesIds.length) {
        filterCollection.filters?.push(HelperService.setFilterArray(["ChannelMetadataType"], this.usedSelectedChannelMetadataTypesIds, "eq"));
      }
      if (this.usedSelectedFoundMetadataTypesIds.length) {
        if (this.usedSelectedFoundMetadataTypesIds.indexOf(-1) >= 0) {
          filterCollection.filters?.push(HelperService.setFilterArray(["FoundMetadataType"], [
            ChannelMetadataType.Phone, ChannelMetadataType.CrmCallCenter
          ], "neq", "and"));
        }else if(this.usedSelectedFoundMetadataTypesIds.indexOf(ChannelMetadataType.Phone) >= 0) {
          filterCollection.filters?.push(HelperService.setFilterArray(["FoundMetadataType"], [
            ChannelMetadataType.Phone, ChannelMetadataType.CrmCallCenter
          ], "eq", "or"));
        } else {
          filterCollection.filters?.push(HelperService.setFilterArray(["FoundMetadataType"], this.usedSelectedFoundMetadataTypesIds, "eq"));
        }
      }

      if (this.usedSelectedCallStatusesIds.length) {
        const statusFilters: FilterCollection = {
          logic: "or",
          filters: [],
        };
        for (const status of this.usedSelectedCallStatusesIds) {
          if (status == -2) {
            statusFilters.filters?.push({
              logic: "and",
              filters: [
                {
                  field: "StatusCall",
                  operator: "isnull",
                },
                {
                  field: "FoundMetadataType",
                  operator: "neq",
                  value: ChannelMetadataType.Phone.toString(),
                },
              ],
            });
          } else if (status == -1) {
            statusFilters.filters?.push({
              logic: "or",
              filters: [
                {
                  field: "StatusCall",
                  operator: "eq",
                  value: MetadataMaskyooStatus.Answer.toString(),
                },
                {
                  field: "StatusCall",
                  operator: "eq",
                  value: MetadataMaskyooStatus.Callback.toString(),
                },
                {
                  field: "StatusCall",
                  operator: "eq",
                  value: MetadataMaskyooStatus.CallMeBackFinished.toString(),
                },
                {
                  field: "StatusCall",
                  operator: "eq",
                  value: MetadataMaskyooStatus.CallRouter.toString(),
                },
                {
                  field: "StatusCall",
                  operator: "eq",
                  value: MetadataMaskyooStatus.NotAnswer.toString(),
                },
              ],
            });
          } else if (status == -3) {
            statusFilters.filters?.push({
              logic: "and",
              filters: [
                {
                  field: "FoundMetadataType",
                  operator: "eq",
                  value: ChannelMetadataType.WhatsAppOrganic.toString(),
                },
                {
                  field: "StatusCall",
                  operator: "isnull",
                },
              ],
            });
          } else if (status == 0 || status == 1) {            
            statusFilters.filters?.push({
              logic: "and",
              filters: [               
                {
                  field: "StatusCall",
                  operator: "eq",
                  value: status.toString(),
                },
                {
                  logic: "or",
                  filters: [
                    {
                      field: "FoundMetadataType",
                      operator: "eq",
                      value: ChannelMetadataType.Phone.toString(),
                    },
                    {
                      field: "FoundMetadataType",
                      operator: "eq",
                      value: ChannelMetadataType.CrmCallCenter.toString(),
                    },
                  ],
                }
              ],
            });
          } else {
            statusFilters.filters?.push({
              logic: "and",
              filters: [
                {
                  field: "FoundMetadataType",
                  operator: "eq",
                  value: ChannelMetadataType.CrmCallCenter.toString(),
                },
                {
                  field: "StatusCall",
                  operator: "eq",
                  value: status.toString(),
                },
              ],
            });
          }
        }
        filterCollection.filters?.push(statusFilters);
      }

      if (this.usedSelectedCommitmentsIds.length) {
        filterCollection.filters?.push(HelperService.setFilterArray(["ProjectPerformanceId"], this.usedSelectedCommitmentsIds, "eq"));
      }
      if (this.usedSelectedAssigneesIds.length) {
        filterCollection.filters?.push(HelperService.setFilterArray(["AssigneeID"], this.usedSelectedAssigneesIds, "eq"));
      }
      if (this.usedSelectedTags.length) {
        const tagTitles: string[] = this.usedSelectedTags.map((i: number | string) => {
          if (typeof i === "number") {
            const foundTag = this.tags.find((j: MultiselectItem) => j.ID as any as number === i);
            if (foundTag) {
              return foundTag.Title;
            }
          } else {
            return i;
          }
          return "";
        });
        filterCollection.filters?.push(HelperService.setFilterArray(["Tag"], tagTitles, "contains"));
      }
      if (this.usedSelectedAssetsIds.length) {
        filterCollection.filters?.push(HelperService.setFilterArray(["AssetId"], this.usedSelectedAssetsIds, "eq"));
      }
      if (this.uniqueByCampaign) {
        filterCollection.filters?.push(HelperService.setFilterArray(["LeadEntryStatusID"], [LeadStatus.Doubled], "neq"));
      }
      if (this.showFavourite) {
        filterCollection.filters?.push(HelperService.setFilterArray(["Favourite"], ["true"], "eq"));
      }
      if (this.usedSelectedAdTitles.length) {
        let selectedAdTitlesAdGroups: string[] = [];
        const selectedAdTitlesAdIds: string[] = [];
        this.usedSelectedAdTitles.forEach((i: string) => {
          const selectedAdTitleValues: string[] = i.split(":");
          selectedAdTitlesAdGroups.push(selectedAdTitleValues[0]);
          selectedAdTitlesAdIds.push(selectedAdTitleValues[1]);
        });
        selectedAdTitlesAdGroups = selectedAdTitlesAdGroups.filter((i: string) => i && i !== "null");
        if (selectedAdTitlesAdGroups.length > 0) {
          filterCollection.filters?.push(HelperService.setFilterArray(["AdGroupIdExternal"], selectedAdTitlesAdGroups, "eq"));
        }
        filterCollection.filters?.push(HelperService.setFilterArray(["AdIdExternal"], selectedAdTitlesAdIds, "eq"));
      }
      if (this.usedSelectedAdGroupsIds.length) {
        const selectedAdTitlesAdGroups = this.usedSelectedAdGroupsIds.filter((i: string) => i && i !== "null");
        if (selectedAdTitlesAdGroups.length > 0) {
          filterCollection.filters?.push(HelperService.setFilterArray(["AdGroupIdExternal"], selectedAdTitlesAdGroups, "eq"));
        }
      }

      if (this.usedSelectedLeadStatusesIds.length) {
        const statusFilters: FilterCollection = {
          logic: "or",
          filters: [],
        };
        for (const status of this.usedSelectedLeadStatusesIds) {
          if (status == LeadStatus.Doubled) {
            statusFilters.filters?.push({
              field: "LeadEntryStatusID",
              operator: "eq",
              value: status.toString(),
            });
          } else {
            statusFilters.filters?.push({
              logic: "and",
              filters: [
                {
                  field: "LeadEntryStatusID",
                  operator: "neq",
                  value: LeadStatus.Doubled.toString(),
                },
              ],
            });
          }
        }
        filterCollection.filters?.push(statusFilters);
      }

      if (this.usedSelectedSaleStatusesIds.length) {
        const statusFilters: FilterCollection = {
          logic: "or",
          filters: [],
        };
        for (const status of this.usedSelectedSaleStatusesIds) {
          if (status === LeadStatus.InProcess) {
            statusFilters.filters?.push({
              field: "IsInProcess",
              operator: "eq",
              value: "true",
            });
          } else if (status === LeadStatus.Closed) {
            statusFilters.filters?.push({
              field: "IsClosed",
              operator: "eq",
              value: "true",
            });
          } else if (status === LeadStatus.Meeting) {
            statusFilters.filters?.push({
              field: "Meeting",
              operator: "eq",
              value: "true",
            });
          } else if (status === LeadStatus.NotGood) {
            statusFilters.filters?.push({
              field: "IsBad",
              operator: "eq",
              value: "true",
            });
          } else if (status === LeadStatus.New) {
            statusFilters.filters?.push({
              logic: "and",
              filters: [
                {
                  field: "IsBad",
                  operator: "eq",
                  value: "false",
                },
                {
                  field: "IsClosed",
                  operator: "eq",
                  value: "false",
                },
                {
                  field: "IsInProcess",
                  operator: "eq",
                  value: "false",
                },
                {
                  field: "Meeting",
                  operator: "eq",
                  value: "false",
                },
              ],
            });
          }
        }
        filterCollection.filters?.push(statusFilters);
      }

      if (this.usedSelectedAdditionalStatusesIds.length) {
        filterCollection.filters?.push(HelperService.setFilterArray(["AdditionalStatusCrmId"], this.usedSelectedAdditionalStatusesIds, "eq"));
      }

      //#endregion Filters


      const filtersString: string = HelperService.getFiltersString(filterCollection);
      const leadsPayload: LeadsPayload = {
        dateFrom: this.usedDatePeriod[0] ? moment(this.usedDatePeriod[0]).format("YYYY-MM-DD") : undefined,
        dateTo: this.usedDatePeriod[1] ? moment(this.usedDatePeriod[1]).format("YYYY-MM-DD") : undefined,
        filter: filtersString,
        kind: this.pageKind,
        page: this.page,
        pageSize: LeadService.getPageSize(),
        priceType: this.usedSelectedPriceType,
        showDeleted: this.showDeleted,
        sort: `${this.sortField}-${this.sortDir}`,
        token: this.token,
        unique: this.uniqueByCampaign,
      };
      this.setLeadsPayload(leadsPayload);
      if (mode === RefreshGridMode.GraphsOnly) {
        if (this.leads.Total && this.leads.Total > this.MAX_LEADS_COUNT_FOR_STATS) {  // do not show stats for large data
          this.setDeactivateStats(true);
          this.setShowGraphs(false);
        } else {
          this.setDeactivateStats(false);
          if (this.showGraphsPermission && this.showGraphs) {
            await this.refreshStatistics(leadsPayload, filtersString);
          }
        }
      } else {
        try {
          await this.getLeads(leadsPayload);
        } finally {
          this.setNeedToRefreshLeadsGrid(RefreshGridMode.None);
        }
        if (this.leads.Total && this.leads.Total > this.MAX_LEADS_COUNT_FOR_STATS) {  // do not show stats for large data
          this.setDeactivateStats(true);
          this.setShowGraphs(false);
        } else {
          this.setDeactivateStats(false);
          if (mode === RefreshGridMode.Full && this.showGraphsPermission && this.show) {
            await this.refreshStatistics(leadsPayload, filtersString);
          }
        }
      }
      Vue.$cookies.set(this.PAGE_FILTERS_COOKIE_NAME, true, "8h");
    });
  }

  private async refreshStatistics(leadsPayload: LeadsPayload, filtersString: string): Promise<void> {
    await this.getLeadsStatisticByAdditionalStatus(leadsPayload); // make one first request to statistics to form cache on server for next requests
    const promises: Promise<LeadStatistic[] | LeadStatisticExtended[]>[] = [];
    promises.push(
      this.getLeadsStatisticByCallStatus(leadsPayload),
      this.getLeadsStatisticByPhoneAndForms(leadsPayload),
      this.getLeadsStatisticBySaleStatus(leadsPayload),
      this.getLeadsStatisticDealMeetingsLeads(leadsPayload)
    );
    if (this.showChannelMetadataType) {
      promises.push(this.getLeadsStatisticChannelMetadataType(leadsPayload));
    }
    
    const leadsStatisticByCampaignsPayload: LeadsStatisticByCampaignsPayload = {
      clientSelected: this.leadsPageData?.ClientId > 0 || this.usedSelectedClientsIds.length > 0,
      dateFrom: moment(this.usedDatePeriod[0]).format("YYYY-MM-DD"),
      dateTo: moment(this.usedDatePeriod[1]).format("YYYY-MM-DD"),
      filter: filtersString,
      token: this.token,
    };
    promises.push(this.getLeadsStatisticByCampaigns(leadsStatisticByCampaignsPayload));
    await Promise.all(promises);
  }

  //#region Setters based on page kind

  setUsedSelectedCallStatusesIds(selectedCallStatusesIds: number[]): void {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        this.setLeadClientsSelectedCallStatusesIds(selectedCallStatusesIds);
        break;
      case LeadsPageKind.Leads:
        this.setSelectedCallStatusesIds(selectedCallStatusesIds);
        break;
    }
  }
  
  setUsedSelectedChannelsIds(selectedChannelsIds: number[]): void {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        this.setLeadClientsSelectedChannelsIds(selectedChannelsIds);
        break;
      case LeadsPageKind.Leads:
        this.setSelectedChannelsIds(selectedChannelsIds);
        break;
    }
  }

  setUsedSelectedChannelMetadataTypesIds(selectedChannelMetadataTypesIds: number[]): void {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        this.setLeadClientsSelectedChannelMetadataTypesIds(selectedChannelMetadataTypesIds);
        break;
      case LeadsPageKind.Leads:
        this.setSelectedChannelMetadataTypesIds(selectedChannelMetadataTypesIds);
        break;
    }
  }

  setUsedSelectedClientsIds(selectedClientsIds: number[]): void {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        this.setLeadClientsSelectedClientsIds(selectedClientsIds);
        break;
      case LeadsPageKind.Leads:
        this.setSelectedClientsIds(selectedClientsIds);
        break;
    }
  }

  setUsedSelectedCommitmentsIds(selectedCommitmentsIds: number[]): void {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        this.setLeadClientsSelectedCommitmentsIds(selectedCommitmentsIds);
        break;
      case LeadsPageKind.Leads:
        this.setSelectedCommitmentsIds(selectedCommitmentsIds);
        break;
    }
  }

  setUsedSelectedFoundMetadataTypesIds(selectedFoundMetadataTypesIds: number[]): void {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        this.setLeadClientsSelectedFoundMetadataTypesIds(selectedFoundMetadataTypesIds);
        break;
      case LeadsPageKind.Leads:
        this.setSelectedFoundMetadataTypesIds(selectedFoundMetadataTypesIds);
        break;
    }
  }

  setUsedSelectedLeadStatusesIds(selectedLeadStatusesIds: LeadStatus[]): void {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        this.setLeadClientsSelectedLeadStatusesIds(selectedLeadStatusesIds);
        break;
      case LeadsPageKind.Leads:
        this.setSelectedLeadStatusesIds(selectedLeadStatusesIds);
        break;
    }
  }

  setUsedSelectedPriceType(selectedPriceType: string): void {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        this.setLeadClientsSelectedPriceType(selectedPriceType);
        break;
      case LeadsPageKind.Leads:
        this.setSelectedPriceType(selectedPriceType);
        break;
    }
  }

  setUsedSelectedSaleStatusesIds(selectedSaleStatusesIds: LeadStatus[]): void {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        this.setLeadClientsSelectedSaleStatusesIds(selectedSaleStatusesIds);
        break;
      case LeadsPageKind.Leads:
        this.setSelectedSaleStatusesIds(selectedSaleStatusesIds);
        break;
    }
  }

  setUsedSelectedTags(selectedTags: string[]): void {
    switch (this.pageKind) {
      case LeadsPageKind.LeadClients:
        this.setLeadClientsSelectedTags(selectedTags);
        break;
      case LeadsPageKind.Leads:
        this.setSelectedTags(selectedTags);
        break;
    }
  }

  //#endregion Setters based on page kind

  //#endregion Private methods
}

</script>

<style lang="scss" scoped>
.page-leads {
  // @media (max-width: 1599px) {
  //   padding-top: 389px;
  //   @media (resolution: 120dpi) {
  //     padding-top: 271px;
  //   }
  // }
  // @media (max-width: 1279px) and (resolution: 120dpi) {
  //   padding-top: 389px;
  // }
  // @media (max-width: 991px) {
  //   padding-top: 162px;
  // }
  // @media (max-width: 359px) {
  //   padding-top: 191px;
  // }
}
</style>
